import { CropConfigV2 } from "@redotech/redo-marketing-templates-common/email-template";
import { memo, useCallback, useRef, useState } from "react";
import ReactCrop, { type Crop, PercentCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { RedoModal } from "../../../../arbiter-components/modal/redo-modal";
import { Flex } from "../../../../flex";
import { ImageEditorModalLeftRail } from "./image-editor-modal-left-rail";
import * as styles from "./image-editor-modal.module.css";

const DEFAULT_CROP_SIZE = 0.8;

interface Props {
  imageUrl: string;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onCropComplete: (crop?: CropConfigV2) => void;
  initialCrop: CropConfigV2 | undefined;
}

// Predefined aspect ratios
const aspectRatios = [
  { label: "Custom", value: "custom", ratio: undefined },
  { label: "16:9 Rectangle", value: "16:9", ratio: 16 / 9 },
  { label: "4:3 Rectangle", value: "4:3", ratio: 4 / 3 },
  { label: "3:4 Rectangle", value: "3:4", ratio: 3 / 4 },
  { label: "2:3 Rectangle", value: "2:3", ratio: 2 / 3 },
  { label: "1:1 Square", value: "1:1", ratio: 1 },
  { label: "Circle", value: "circle", ratio: 1 },
] as const;

export type AspectRatioOption = (typeof aspectRatios)[number]["value"];

function redoCropToNpmCrop(crop: CropConfigV2 | undefined): Crop | undefined {
  if (!crop) {
    return undefined;
  }
  return {
    unit: "%",
    x: crop.xRatio * 100,
    y: crop.yRatio * 100,
    width: crop.widthRatio * 100,
    height: crop.heightRatio * 100,
  };
}

function npmCropToRedoCrop(
  crop: Crop | undefined,
  circularCrop: boolean,
  baseImage: { width: number; height: number },
): CropConfigV2 | undefined {
  if (!crop) {
    return undefined;
  }

  return {
    xRatio: crop.x / 100,
    yRatio: crop.y / 100,
    widthRatio: crop.width / 100,
    heightRatio: crop.height / 100,
    circularCrop,
    baseImage,
  };
}

function ratioToAspectRatioOption(
  ratio: number | undefined,
): AspectRatioOption {
  if (!ratio) {
    return "custom";
  }
  // allow for a tiny bit of floating point error
  const epsilon = 0.001;
  const ratioOption = aspectRatios.find(
    (option) => option.ratio && Math.abs(option.ratio - ratio) < epsilon,
  );
  return ratioOption ? ratioOption.value : "custom";
}

export const ImageEditorModal = memo(function ImageEditorModal({
  imageUrl,
  isOpen,
  setIsOpen,
  onCropComplete,
  initialCrop,
}: Props) {
  const [lockAspectRatio, setLockAspectRatio] = useState(
    initialCrop?.circularCrop || false,
  );
  const [cropInProgress, setCropInProgress] = useState<
    CropConfigV2 | undefined
  >(initialCrop);
  const [imageDimensions, setImageDimensions] = useState<
    | {
        natural: { width: number; height: number };
        screen: { width: number; height: number };
      }
    | undefined
  >();
  const imgRef = useRef<HTMLImageElement>(null);

  const cropAspectRatio =
    cropInProgress && imageDimensions
      ? (cropInProgress.widthRatio * imageDimensions.natural.width) /
        (cropInProgress.heightRatio * imageDimensions.natural.height)
      : undefined;

  const aspectRatioOption = ratioToAspectRatioOption(cropAspectRatio);

  const onNpmCropChange = (npmCrop: PercentCrop) => {
    if (!imageDimensions) {
      return;
    }

    const newCropConfig = npmCropToRedoCrop(
      npmCrop,
      cropInProgress?.circularCrop || false,
      imageDimensions.natural,
    );

    setCropInProgress(newCropConfig);
  };

  // Save the crop configuration
  const saveCropConfig = useCallback(() => {
    // if the crop has zero area, consider it as no crop
    if (cropInProgress?.widthRatio === 0 || cropInProgress?.heightRatio === 0) {
      setCropInProgress(undefined);
      onCropComplete(undefined);
      setIsOpen(false);
      return;
    }
    // if the crop has the full image area, consider it as no crop
    if (cropInProgress?.widthRatio === 1 && cropInProgress?.heightRatio === 1) {
      setCropInProgress(undefined);
      onCropComplete(undefined);
      setIsOpen(false);
      return;
    }
    if (cropInProgress) {
      onCropComplete(cropInProgress);
    }
    setIsOpen(false);
  }, [cropInProgress, onCropComplete, setIsOpen]);

  const onImageLoad = useCallback(() => {
    if (imgRef.current) {
      const naturalDimensions = {
        width: imgRef.current.naturalWidth,
        height: imgRef.current.naturalHeight,
      };

      setImageDimensions({
        natural: naturalDimensions,
        screen: {
          width: imgRef.current.clientWidth,
          height: imgRef.current.clientHeight,
        },
      });

      // If no initial crop exists, default to the full image
      if (!cropInProgress) {
        setCropInProgress({
          xRatio: 0,
          yRatio: 0,
          widthRatio: 1,
          heightRatio: 1,
          circularCrop: false,
          baseImage: naturalDimensions,
        });
      }
    }
  }, [imgRef, cropInProgress]);

  const cropHeightPx = Math.round(
    (cropInProgress?.heightRatio ?? 0) * (imageDimensions?.natural.height || 0),
  );
  const cropWidthPx = Math.round(
    (cropInProgress?.widthRatio ?? 0) * (imageDimensions?.natural.width || 0),
  );

  const handleDimensionChange = (
    newValue: number,
    dimension: "width" | "height",
  ) => {
    setCropInProgress((prevCrop) => {
      if (!prevCrop || !imageDimensions) {
        return prevCrop;
      }

      let newWidthRatio, newHeightRatio;

      if (dimension === "width") {
        newWidthRatio = newValue / imageDimensions.natural.width;

        // If aspect ratio is locked, calculate new height based on width
        if (lockAspectRatio && cropAspectRatio) {
          const newHeightPx = newValue / cropAspectRatio;
          newHeightRatio = newHeightPx / imageDimensions.natural.height;
        } else {
          newHeightRatio = prevCrop.heightRatio;
        }
      } else {
        newHeightRatio = newValue / imageDimensions.natural.height;

        // If aspect ratio is locked, calculate new width based on height
        if (lockAspectRatio && cropAspectRatio) {
          const newWidthPx = newValue * cropAspectRatio;
          newWidthRatio = newWidthPx / imageDimensions.natural.width;
        } else {
          newWidthRatio = prevCrop.widthRatio;
        }
      }

      let newXRatio = prevCrop.xRatio;
      let newYRatio = prevCrop.yRatio;

      // If the new width would exceed bounds, expand in the opposite direction
      if (prevCrop.xRatio + newWidthRatio > 1) {
        newXRatio = Math.max(0, 1 - newWidthRatio);
      }

      // If the new height would exceed bounds, expand in the opposite direction
      if (prevCrop.yRatio + newHeightRatio > 1) {
        newYRatio = Math.max(0, 1 - newHeightRatio);
      }

      return {
        ...prevCrop,
        widthRatio: Math.min(newWidthRatio, 1),
        heightRatio: Math.min(newHeightRatio, 1),
        xRatio: newXRatio,
        yRatio: newYRatio,
      };
    });
  };

  const handleWidthChange = (width: number) =>
    handleDimensionChange(width, "width");
  const handleHeightChange = (height: number) =>
    handleDimensionChange(height, "height");

  return (
    <RedoModal
      buttonPlacement="tight"
      contentScrollable
      footerBorder
      headerBorder
      isOpen={isOpen}
      mainContentClassName={styles.container}
      modalSize="lg"
      onModalCloseRequested={() => setIsOpen(false)}
      primaryButton={{ text: "Save", onClick: saveCropConfig }}
      secondaryButton={{ text: "Cancel", onClick: () => setIsOpen(false) }}
      title="Crop image"
    >
      <Flex dir="row" style={{ height: "100%" }}>
        <ImageEditorModalLeftRail
          aspectRatio={aspectRatioOption}
          height={cropHeightPx || 0}
          lockAspectRatio={lockAspectRatio}
          selectAspectRatio={(ratio: AspectRatioOption) => {
            const selectedAspect = aspectRatios.find(
              (option) => option.value === ratio,
            );
            if (selectedAspect && imageDimensions) {
              // Set the lock state based on the selection
              const shouldLock = ratio !== "custom";
              setLockAspectRatio(shouldLock);

              if (ratio === "custom") {
                // For custom, just unlock - keep current crop
                return;
              }

              // For preset ratios, create a centered crop with that aspect ratio
              const targetAspectRatio = selectedAspect.ratio || 1;
              const imageAspectRatio =
                imageDimensions.natural.width / imageDimensions.natural.height;

              // Start with 80% of the image and adjust to fit the aspect ratio
              let cropWidth, cropHeight;
              if (targetAspectRatio > imageAspectRatio) {
                // Target is wider than image - constrain by width
                cropWidth = DEFAULT_CROP_SIZE;
                cropHeight =
                  (DEFAULT_CROP_SIZE / targetAspectRatio) * imageAspectRatio;
              } else {
                // Target is taller than image - constrain by height
                cropHeight = DEFAULT_CROP_SIZE;
                cropWidth =
                  (DEFAULT_CROP_SIZE * targetAspectRatio) / imageAspectRatio;
              }

              setCropInProgress({
                xRatio: (1 - cropWidth) / 2, // Center horizontally
                yRatio: (1 - cropHeight) / 2, // Center vertically
                widthRatio: cropWidth,
                heightRatio: cropHeight,
                circularCrop: ratio === "circle",
                baseImage: imageDimensions.natural,
              });
            }
          }}
          setHeight={handleHeightChange}
          setLockAspectRatio={setLockAspectRatio}
          setWidth={handleWidthChange}
          width={cropWidthPx || 0}
        />

        <Flex
          style={{
            flex: 1,
            padding: "var(--redo-spacing-md)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            overflow: "hidden",
          }}
        >
          <ReactCrop
            aspect={lockAspectRatio ? cropAspectRatio : undefined}
            circularCrop={cropInProgress?.circularCrop}
            crop={redoCropToNpmCrop(cropInProgress)}
            keepSelection
            onChange={(_, percentCrop) => onNpmCropChange(percentCrop)}
            ruleOfThirds
          >
            <img
              alt="Image to crop"
              onLoad={onImageLoad}
              ref={imgRef}
              src={imageUrl}
              style={{
                maxWidth: "100%",
                maxHeight: "600px",
                display: "block",
                margin: "var(--redo-spacing-none) auto",
              }}
            />
          </ReactCrop>
        </Flex>
      </Flex>
    </RedoModal>
  );
});
